<template>
  <div class="metrics-table-wrapper">
    <div v-if="!sortedMonths.length" class="text-muted p-3">
      {{ $t('brand.opportunities.no-jobs') }}
    </div>
    <table
      v-else
      class="table table-sm table-bordered table-hover metrics-table"
    >
      <thead>
        <tr class="bg-light">
          <th rowspan="2" class="align-middle month-col">
            {{ $t('brand.opportunities.month') }}
          </th>
          <th
            v-for="type in activeJobTypes"
            :key="type"
            colspan="3"
            class="text-center border-left"
          >
            {{ jobTypeLabel(type) }}
          </th>
          <th colspan="3" class="text-center border-left font-weight-bold">
            {{ $t('brand.opportunities.total') }}
          </th>
        </tr>
        <tr class="bg-light">
          <template v-for="type in activeJobTypes">
            <th
              :key="`${type}-count`"
              class="text-right sub-header border-left"
            >
              {{ $t('brand.opportunities.count-abbr') }}
            </th>
            <th :key="`${type}-rev`" class="text-right sub-header">
              {{ $t('brand.opportunities.revenue-abbr') }}
            </th>
            <th :key="`${type}-cost`" class="text-right sub-header">
              {{ $t('brand.opportunities.cost-abbr') }}
            </th>
          </template>
          <th class="text-right sub-header border-left">
            {{ $t('brand.opportunities.count-abbr') }}
          </th>
          <th class="text-right sub-header">
            {{ $t('brand.opportunities.revenue-abbr') }}
          </th>
          <th class="text-right sub-header">
            {{ $t('brand.opportunities.cost-abbr') }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="month in sortedMonths" :key="month">
          <td class="font-weight-bold month-col">{{ month }}</td>
          <template v-for="type in activeJobTypes">
            <td :key="`${month}-${type}-count`" class="text-right border-left">
              {{ cellData(month, type).count || '-' }}
            </td>
            <td :key="`${month}-${type}-rev`" class="text-right">
              {{ cellData(month, type).revenue | currency }}
            </td>
            <td :key="`${month}-${type}-cost`" class="text-right">
              {{ cellData(month, type).cost | currency }}
            </td>
          </template>
          <td class="text-right font-weight-bold border-left">
            {{ monthTotals(month).count }}
          </td>
          <td class="text-right font-weight-bold">
            {{ monthTotals(month).revenue | currency }}
          </td>
          <td class="text-right font-weight-bold">
            {{ monthTotals(month).cost | currency }}
          </td>
        </tr>
      </tbody>
      <tfoot>
        <tr class="bg-light font-weight-bold">
          <td class="month-col">{{ $t('brand.opportunities.grand-total') }}</td>
          <template v-for="type in activeJobTypes">
            <td :key="`total-${type}-count`" class="text-right border-left">
              {{ typeTotals(type).count }}
            </td>
            <td :key="`total-${type}-rev`" class="text-right">
              {{ typeTotals(type).revenue | currency }}
            </td>
            <td :key="`total-${type}-cost`" class="text-right">
              {{ typeTotals(type).cost | currency }}
            </td>
          </template>
          <td class="text-right border-left">{{ grandTotal.count }}</td>
          <td class="text-right">{{ grandTotal.revenue | currency }}</td>
          <td class="text-right">{{ grandTotal.cost | currency }}</td>
        </tr>
      </tfoot>
    </table>
  </div>
</template>

<script>
const EMPTY_CELL = { count: 0, revenue: 0, cost: 0 };

export default {
  name: 'OpportunityMetricsTable',
  props: {
    metrics: {
      type: Object,
      required: true,
    },
    activeJobTypes: {
      type: Array,
      required: true,
    },
  },
  computed: {
    sortedMonths() {
      return Object.keys(this.metrics.months || {}).sort();
    },
    grandTotal() {
      const totals = this.metrics.totals || {};
      let count = 0;
      let revenue = 0;
      let cost = 0;
      for (const t of Object.values(totals)) {
        count += t.count;
        revenue += t.revenue;
        cost += t.cost;
      }
      return { count, revenue, cost };
    },
  },
  methods: {
    jobTypeLabel(type) {
      const key = `brand.opportunities.job-types.${type}`;
      const translated = this.$t(key);
      return translated !== key ? translated : type;
    },
    cellData(month, type) {
      const monthData = this.metrics.months?.[month];
      return monthData?.[type] || EMPTY_CELL;
    },
    monthTotals(month) {
      const monthData = this.metrics.months?.[month] || {};
      let count = 0;
      let revenue = 0;
      let cost = 0;
      for (const t of Object.values(monthData)) {
        count += t.count;
        revenue += t.revenue;
        cost += t.cost;
      }
      return { count, revenue, cost };
    },
    typeTotals(type) {
      return this.metrics.totals?.[type] || EMPTY_CELL;
    },
  },
};
</script>

<style scoped>
.metrics-table-wrapper {
  overflow-x: auto;
}

.metrics-table {
  font-size: 0.85rem;
  white-space: nowrap;
}

.month-col {
  min-width: 80px;
}

.sub-header {
  font-size: 0.75rem;
  font-weight: normal;
  color: #6c757d;
}
</style>
